import { useEffect, useState } from 'react';
import {
  ImportPollingStatus,
  ImportStatus,
  usePollForImportStatus,
} from '../../../hooks/usePollForImportStatus';
import { AnchorAPI } from '../../../modules/AnchorAPI';

export function useEpisodeListImport(
  webStationId: string | null,
  handleFetchEpisodes: () => void
) {
  const [importErrors, setImportErrors] = useState<ImportStatus['errors']>([]);
  const [metadata, setMetadata] = useState<{
    externalRssFeed?: string;
    podcastImage?: string;
  } | null>(null);
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const importState = usePollForImportStatus(webStationId);
  const {
    importStatus: {
      status: importPollingStatus,
      percentageComplete,
      totalEpisodes,
      errors,
    },
    pollForImportStatus,
  } = importState;

  useEffect(() => {
    if (
      importPollingStatus === ImportPollingStatus.NOT_FOUND ||
      importPollingStatus === ImportPollingStatus.COMPLETE
    ) {
      setImportErrors(errors);
      // if we imported episodes, re-fetch the user's episode list to show
      // them the newly imported episodes
      if (isImporting) {
        handleFetchEpisodes();
      }
    }
  }, [importPollingStatus, errors, handleFetchEpisodes, isImporting]);

  /**
   * if polling function shows an `IN_PROGRESS` status, that means there are
   * external episodes that we're actively importing. we want to set this
   * as a flag in state so later when the importing is finished, we know to
   * re-fetch the user's episodes to refresh their episode list
   */
  useEffect(() => {
    if (importPollingStatus === ImportPollingStatus.IN_PROGRESS) {
      setIsImporting(true);
    }
  }, [importPollingStatus]);

  useEffect(() => {
    AnchorAPI.fetchMyPodcastMetadata().then(res => {
      const { podcastExternalSource, podcastImage } = res;
      /** for these imported users, we assume they always have their imported
       * external rss feed and podcast image
       */
      setMetadata({
        externalRssFeed: podcastExternalSource,
        podcastImage,
      });
    });
  }, []);

  function removeImportItem(index: number) {
    const newImportErrors = [...importErrors];
    newImportErrors.splice(index, 1);
    setImportErrors(newImportErrors);
  }

  return {
    importPollingStatus,
    percentageComplete,
    totalEpisodes,
    importErrors,
    pollForImportStatus,
    removeImportItem,
    podcastImage: metadata ? metadata.podcastImage : undefined,
    externalRssFeed: metadata ? metadata.externalRssFeed : undefined,
  };
}
